import React, { useEffect, useState } from "react";
import { fetchDailyTurnover } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import PropTypes from 'prop-types';
import { CircularProgress, Box } from "@mui/material";
import { useCustomerCurrency } from "../context/CustomerSettingsContext";
import { useTranslation } from "react-i18next";
const WorkcellDailyTurnoverChart = ({ date, workcellId, onClick, activeIndex, onHover, loading, onLoadingChange }) => {
    const [data, setData] = useState([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const { currency, currencySymbol, formatCurrency } = useCustomerCurrency();
    const { t } = useTranslation();

  useEffect(() => {
    if (!date || !workcellId) {
      setData([]);
      setInternalLoading(false);
      if (typeof onLoadingChange === 'function') onLoadingChange(false);
      return;
    }
    let mounted = true;
    setInternalLoading(true);
    const callParent = typeof onLoadingChange === 'function';
    if (callParent) onLoadingChange(true);
    fetchDailyTurnover(date)
      .then((apiData) => {
        if (!mounted) return;
        if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
          setData([]);
          return;
        }
        // Filter for this workcell
        const filtered = apiData.filter(row => String(row.workcell_id) === String(workcellId));
        if (filtered.length === 0) {
          setData([]);
          return;
        }
        // Build full 24-hour data with all turnover fields
        const fullDayData = Array.from({ length: 24 }, (_, i) => ({
          hour: i.toString().padStart(2, "0"),
          operation_turnover: 0,
          hourly_turnover: 0,
          total_turnover: 0
        }));
        filtered.forEach((entry) => {
          const index = fullDayData.findIndex((d) => d.hour === entry.hour);
          if (index !== -1) {
            fullDayData[index].operation_turnover = entry.operation_turnover || 0;
            fullDayData[index].hourly_turnover = entry.hourly_turnover || 0;
            fullDayData[index].total_turnover = entry.total_turnover || 0;
          }
        });
        setData(fullDayData);
      })
      .catch(() => {
        if (!mounted) return;
        setData([]);
      })
      .finally(() => {
        if (!mounted) return;
        setInternalLoading(false);
        if (callParent) onLoadingChange(false);
      });
    return () => { mounted = false; };
  }, [date, workcellId, onLoadingChange]);

    const isLoading = typeof loading === 'boolean' ? loading : internalLoading;

    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      );
    }

    if (data.length === 0)
      return <div>{t("workcell.monthlyOverview.turnover.noData")}</div>;
    // Colors
    const opColor = '#ff9800'; // orange
    const hrColor = '#1976d2'; // blue
    const totalColor = '#222'; // black
    return (
      <div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={data}
            syncId="workcellCharts"
          >
            <XAxis dataKey="hour" />
            <YAxis
              label={{
                value: t("workcell.dailyOverview.turnover.axisLabel", { currency: currencySymbol }),
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" }
              }}
            />
            <Tooltip
              formatter={(value) => formatCurrency(value, { fallback: "-" })}
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const d = data.find(row => row.hour === label);
                return (
                  <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10 }}>
                    <div><b>{t("daily.plannings.throughputChart.tooltip.hour")}:</b> {label}:00</div>
                    <div><span style={{ display: 'inline-block', width: 12, height: 12, background: opColor, marginRight: 6, borderRadius: 2 }}></span><b>{t("workcell.monthlyOverview.turnover.tooltip.operation")}:</b> {formatCurrency(d?.operation_turnover, { fallback: "-" })}</div>
                    <div><span style={{ display: 'inline-block', width: 12, height: 12, background: hrColor, marginRight: 6, borderRadius: 2 }}></span><b>{t("workcell.monthlyOverview.turnover.tooltip.hourly")}:</b> {formatCurrency(d?.hourly_turnover, { fallback: "-" })}</div>
                    <div><span style={{ display: 'inline-block', width: 12, height: 12, background: totalColor, marginRight: 6, borderRadius: 2 }}></span><b>{t("workcell.monthlyOverview.turnover.tooltip.total")}:</b> {formatCurrency(d?.total_turnover, { fallback: "-" })}</div>
                  </div>
                );
              }}
            />
            <Bar
              dataKey="operation_turnover"
              stackId="a"
              fill={opColor}
              name={t("workcell.monthlyOverview.turnover.legend.operation", { currency })}
              cursor="pointer"
              onClick={(data, index) => {
                if (onClick) onClick(data.hour);
              }}
            />
            <Bar
              dataKey="hourly_turnover"
              stackId="a"
              fill={hrColor}
              name={t("workcell.monthlyOverview.turnover.legend.hourly", { currency })}
              cursor="pointer"
              onClick={(data, index) => {
                if (onClick) onClick(data.hour);
              }}
            />
            <Legend />
          </BarChart>
        </ResponsiveContainer>
      </div>
  );
}

WorkcellDailyTurnoverChart.propTypes = {
  date: PropTypes.string.isRequired,
  workcellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClick: PropTypes.func,
  activeIndex: PropTypes.number,
  onHover: PropTypes.func,
  loading: PropTypes.bool,
  onLoadingChange: PropTypes.func,
};

export default WorkcellDailyTurnoverChart;
